import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useCourse } from '../../context/CourseContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  PenLine,
  Save,
  Sparkles,
  Clock,
  CheckCircle2,
} from 'lucide-react';

export const JournalEntry = ({ chapter }) => {
  const { saveJournalEntry, getJournalEntry, journalEntries } = useCourse();
  const [entry, setEntry] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load existing entry
  useEffect(() => {
    const savedEntry = getJournalEntry(chapter.id);
    setEntry(savedEntry);
  }, [chapter.id, getJournalEntry]);

  // Track unsaved changes
  useEffect(() => {
    const savedEntry = getJournalEntry(chapter.id);
    setHasUnsavedChanges(entry !== savedEntry && entry.length > 0);
  }, [entry, chapter.id, getJournalEntry]);

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save delay
    await new Promise(resolve => setTimeout(resolve, 500));
    saveJournalEntry(chapter.id, entry);
    setIsSaving(false);
    setHasUnsavedChanges(false);
    toast.success('Journal entry saved!', {
      description: 'Your reflection has been saved successfully.',
    });
  };

  const savedAt = journalEntries[chapter.id]?.savedAt;
  const formattedDate = savedAt 
    ? new Date(savedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-card border border-silver/20 rounded-xl p-6 shadow-sm hover:shadow-silver transition-shadow"
    >
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-maroon/20 flex items-center justify-center flex-shrink-0 shadow-maroon">
          <PenLine className="w-6 h-6 text-maroon" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-xl font-semibold text-foreground mb-1">
            Reflection Journal
          </h3>
          <p className="text-sm text-muted-foreground">
            Take a moment to reflect on what you've learned. Writing helps integrate insights.
          </p>
        </div>
      </div>

      {/* Prompt */}
      <div className="bg-gradient-to-br from-silver-shimmer/20 to-background rounded-lg p-4 mb-6 border border-silver/20">
        <div className="flex items-start gap-2">
          <Sparkles className="w-5 h-5 text-maroon flex-shrink-0 mt-0.5" />
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-maroon block mb-1">
              Reflection Prompt
            </span>
            <p className="text-foreground leading-relaxed">
              {chapter.reflection.prompt}
            </p>
          </div>
        </div>
      </div>

      {/* Textarea */}
      <div className="relative mb-4">
        <Textarea
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
          placeholder={chapter.reflection.placeholder}
          className="journal-textarea min-h-[250px] text-base"
        />
        {hasUnsavedChanges && (
          <div className="absolute top-3 right-3">
            <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded">
              Unsaved changes
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{entry.length} characters</span>
          {formattedDate && (
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Last saved {formattedDate}
            </span>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasUnsavedChanges || isSaving}
          className="btn-gold gap-2"
        >
          {isSaving ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <Save className="w-4 h-4" />
              </motion.div>
              Saving...
            </>
          ) : hasUnsavedChanges ? (
            <>
              <Save className="w-4 h-4" />
              Save Entry
            </>
          ) : (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Saved
            </>
          )}
        </Button>
      </div>

      {/* Writing Tips */}
      <div className="mt-6 pt-6 border-t border-silver/20">
        <h4 className="text-sm font-medium text-foreground mb-3">Writing Tips</h4>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <span className="text-maroon">•</span>
            Write freely without judgment
          </li>
          <li className="flex items-start gap-2">
            <span className="text-maroon">•</span>
            Be honest with yourself
          </li>
          <li className="flex items-start gap-2">
            <span className="text-maroon">•</span>
            Include specific examples
          </li>
          <li className="flex items-start gap-2">
            <span className="text-maroon">•</span>
            Notice emotions that arise
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default JournalEntry;
